'use client';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { Box, CircularProgress } from '@mui/material';

import { EmptyState, ErrorState, HomeSkeleton } from '@/components';
import { useT } from '@/hooks';
import type { Market } from '@/types';
import { FilterPanel } from './FilterPanel';
import { MarketListByTab } from './MarketListByTab';

interface HomeMarketsDisplayProps {
  isLoading: boolean;
  error: unknown;
  markets: Market[];
  refetch: () => void;
  isFilterOpen: boolean;
  sortBy: string;
  hideSports: boolean;
  onSortByChange: (value: string) => void;
  onHideSportsChange: (value: boolean) => void;
  activeCategorySlug: string;
  onTradeClick: (marketId: string, outcomeId: string, isBuyOrder: boolean) => void;
  pagination?: {
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
  };
  onFetchNextPage: () => void;
}

export const HomeMarketsDisplay = ({
  isLoading,
  error,
  markets,
  refetch,
  isFilterOpen,
  sortBy,
  hideSports,
  onSortByChange,
  onHideSportsChange,
  activeCategorySlug,
  onTradeClick,
  pagination,
  onFetchNextPage,
}: HomeMarketsDisplayProps) => {
  const { t } = useT();
  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && pagination?.hasNextPage && !pagination.isFetchingNextPage) {
      onFetchNextPage();
    }
  }, [inView, pagination?.hasNextPage, pagination?.isFetchingNextPage, onFetchNextPage]);

  if (isLoading) {
    return <HomeSkeleton />;
  }

  if (error) {
    return (
      <ErrorState title="Failed to load markets" message="Please try again." onRetry={refetch} />
    );
  }

  if (!markets || markets.length === 0) {
    return (
      <EmptyState
        title={t('sports.noMarketsAvailable', { defaultValue: 'No markets available' })}
      />
    );
  }

  return (
    <>
      {isFilterOpen && (
        <FilterPanel
          sortBy={sortBy}
          hideSports={hideSports}
          onSortByChange={onSortByChange}
          onHideSportsChange={onHideSportsChange}
        />
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: 2,
        }}
      >
        <MarketListByTab
          activeCategorySlug={activeCategorySlug}
          markets={markets}
          handleTradeClick={onTradeClick}
        />
      </Box>

      {pagination?.hasNextPage && (
        <Box
          ref={ref}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 4,
            mb: 2,
            width: '100%',
            py: 2,
          }}
        >
          {pagination.isFetchingNextPage && <CircularProgress size={30} />}
        </Box>
      )}
    </>
  );
};
